import { useEffect, useState } from "react";
import axios from "axios";
import { Star, MapPin, RefreshCw, Loader2, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GbpSyncTab() {
  const [sedi, setSedi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const fetchSedi = async () => {
    try {
      const r = await axios.get(`${API}/gbp/sedi`);
      setSedi(r.data?.sedi || []);
    } catch {
      setSedi([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSedi(); }, []);

  const triggerSync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await axios.post(`${API}/gbp/admin/sync-now`, {}, { withCredentials: true });
      setSyncResult(r.data);
      await fetchSedi();
    } catch (e) {
      setSyncResult({ ok: false, error: e?.response?.data?.detail || "Errore sync" });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Carico...</div>;
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-gbp-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-zinc-900 mb-1 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#163321]" />
            Google Business Profile (Local SEO)
          </h3>
          <p className="text-sm text-zinc-500">
            Sync notturno automatico delle 3 sedi: rating, recensioni, orari, stato apertura.
            Iniezione automatica in JSON-LD homepage e pagine sede.
          </p>
        </div>
        <button
          onClick={triggerSync}
          disabled={syncing}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-[#163321] text-white text-sm hover:bg-[#224A32] disabled:opacity-50"
          data-testid="gbp-sync-now-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {syncing ? "Sync in corso..." : "Sync ora"}
        </button>
      </div>

      {syncResult && (
        <div className={`rounded-lg p-3 text-sm border ${
          syncResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"
        }`} data-testid="gbp-sync-result">
          {syncResult.ok ? (
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Sync completato: {syncResult.results?.filter(r=>r.ok).length || 0}/{syncResult.results?.length || 0} sedi aggiornate</span>
          ) : (
            <span className="inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Errore: {syncResult.error}</span>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {sedi.map(s => (
          <div key={s.slug} className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={`gbp-sede-${s.slug}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">{s.slug}</p>
                <h4 className="font-semibold text-zinc-900">{s.label}</h4>
              </div>
              {s.open_now === true && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Aperto</span>}
              {s.open_now === false && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">Chiuso</span>}
            </div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl font-bold text-[#163321]">{s.rating?.toFixed(1)}</span>
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`w-3 h-3 ${i <= Math.round(s.rating) ? 'text-amber-400 fill-amber-400' : 'text-zinc-300'}`} />
                ))}
              </div>
              <span className="text-xs text-zinc-500">({s.review_count} totali)</span>
            </div>
            <p className="text-xs text-zinc-500 mb-1">Recensioni cached: <strong>{s.reviews?.length || 0}</strong></p>
            <p className="text-xs text-zinc-500">Last sync: {s.synced_at?.slice(0,16).replace('T', ' ')}</p>
            {s.google_maps_url && (
              <a href={s.google_maps_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-xs text-[#163321] hover:underline">
                Vedi su Google Maps <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-zinc-400 italic">
        Sync automatico ogni 24h. Quote Places API: ~3 calls/giorno (limite gratuito 100k/mese).
      </p>
    </div>
  );
}
